import axios, { type AxiosError, type AxiosInstance } from 'axios';
import type { ApiClientConfig, RequestConfig } from './types';

type RefreshQueueItem = {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

export function createApiClient(config: ApiClientConfig) {
  const {
    baseURL,
    tokenStorage,
    refreshTokenUrl,
    refreshTokenFn,
    defaultHeaders = {},
    timeout = 30000,
    tenantId: tenantIdConfig,
    onUnauthorized,
    getLanguage,
  } = config;

  const getAcceptLanguage = (): string =>
    getLanguage?.() ?? (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US');

  const client: AxiosInstance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      ...defaultHeaders,
    },
    // 304 Not Modified é sucesso (cache); sem isso o Axios rejeita e cai no interceptor de erro sem tentar refresh.
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  });

  let isRefreshing = false;
  let refreshQueue: RefreshQueueItem[] = [];
  let lastSuccessfulRefresh = 0;
  const REFRESH_COOLDOWN_MS = 5000; // Não tenta outro refresh dentro de 5s após sucesso

  function processQueue(
    error: Error | null,
    newAccessToken: string | null = null
  ) {
    refreshQueue.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(newAccessToken);
    });
    refreshQueue = [];
  }

  async function refreshAccessToken(): Promise<string> {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      tokenStorage.clearTokens();
      throw new Error('No refresh token available');
    }

    if (refreshTokenFn) {
      const { accessToken, refreshToken: newRefreshToken } =
        await refreshTokenFn(refreshToken);
      tokenStorage.setTokens(accessToken, newRefreshToken);
      return accessToken;
    }

    if (refreshTokenUrl) {
      const accessToken = tokenStorage.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/plain',
        'Accept-Language': getAcceptLanguage(),
      };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      const tenantId =
        typeof tenantIdConfig === 'function' ? tenantIdConfig() : tenantIdConfig;
      if (tenantId) {
        headers['tenant-id'] = tenantId;
      }
      const res = await axios.post<{ accessToken?: string; refreshToken?: string; token?: string } | string>(
        refreshTokenUrl,
        { refreshToken },
        {
          baseURL,
          headers,
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        }
      );

      // 304 Not Modified ou 204 No Content: servidor indica que o token segue válido; reutilizar o atual.
      if (res.status === 304 || res.status === 204 || !res.data) {
        const current = tokenStorage.getAccessToken();
        if (current) {
          lastSuccessfulRefresh = Date.now();
          return current;
        }
        throw new Error('Refresh endpoint returned no content');
      }

      const data = res.data;
      let newAccessToken: string;
      let newRefreshToken: string;

      if (typeof data === 'string' && data.trim()) {
        // Resposta é string (o token direto)
        newAccessToken = data;
        newRefreshToken = refreshToken;
      } else if (data && typeof data === 'object') {
        // Resposta é objeto - aceita accessToken ou token
        const tokenValue = data.accessToken ?? (data as Record<string, unknown>).token;
        if (typeof tokenValue === 'string' && tokenValue.trim()) {
          newAccessToken = tokenValue;
          newRefreshToken = typeof data.refreshToken === 'string' && data.refreshToken.trim() 
            ? data.refreshToken 
            : refreshToken;
        } else {
          throw new Error('Invalid refresh token response: missing or empty accessToken');
        }
      } else {
        throw new Error('Invalid refresh token response format');
      }

      tokenStorage.setTokens(newAccessToken, newRefreshToken);
      lastSuccessfulRefresh = Date.now();
      return newAccessToken;
    }

    throw new Error('Neither refreshTokenUrl nor refreshTokenFn was provided');
  }

  client.interceptors.request.use(
    reqConfig => {
      const cfg = reqConfig as RequestConfig & {
        headers: Record<string, string>;
        data?: unknown;
      };
      if (cfg.skipAuth) return reqConfig;

      // FormData: deixar o axios definir Content-Type com boundary (multipart/form-data)
      if (cfg.data instanceof FormData && cfg.headers) {
        delete cfg.headers['Content-Type'];
      }

      const token = tokenStorage.getAccessToken();
      if (token) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }

      const tenantId =
        typeof tenantIdConfig === 'function'
          ? tenantIdConfig()
          : tenantIdConfig;
      if (tenantId) {
        cfg.headers['tenant-id'] = tenantId;
      }

      cfg.headers['Accept-Language'] = getAcceptLanguage();

      return reqConfig;
    },
    error => Promise.reject(error)
  );

  /**
   * Reconstrói e reenvia a requisição que falhou, usando o novo access token.
   * Extraímos apenas as propriedades essenciais do config original para evitar
   * problemas com propriedades internas do axios.
   */
  function retryWithToken(
    originalConfig: RequestConfig & { _retry?: boolean },
    newAccessToken: string
  ) {
    const method = (originalConfig.method || 'get').toLowerCase();
    const url = originalConfig.url || '';

    const newHeaders: Record<string, string> = {};

    // Copia headers originais (exceto Authorization que será substituído)
    if (originalConfig.headers) {
      const srcHeaders = originalConfig.headers as Record<string, unknown>;
      for (const key of Object.keys(srcHeaders)) {
        if (key.toLowerCase() !== 'authorization' && typeof srcHeaders[key] === 'string') {
          newHeaders[key] = srcHeaders[key] as string;
        }
      }
    }

    // Define o novo token
    newHeaders['Authorization'] = `Bearer ${newAccessToken}`;

    // Adiciona tenant-id se configurado
    const tenantId =
      typeof tenantIdConfig === 'function' ? tenantIdConfig() : tenantIdConfig;
    if (tenantId) {
      newHeaders['tenant-id'] = tenantId;
    }

    const retryConfig: RequestConfig & { _retry: boolean } = {
      url,
      method,
      headers: newHeaders,
      params: originalConfig.params,
      data: originalConfig.data,
      timeout: originalConfig.timeout,
      _retry: true,
      skipAuth: true, // Já setamos o token; evita que o interceptor sobrescreva
    };

    return client.request(retryConfig);
  }

  client.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (RequestConfig & { _retry?: boolean }) | undefined;

      // Só trata 401 (não autorizado). 404 e outros erros apenas rejeitam.
      const isUnauthorized = error.response?.status === 401;
      if (!isUnauthorized) {
        return Promise.reject(error);
      }

      // Já é retry desta mesma requisição → não tentar refresh de novo, rejeitar.
      if (originalRequest?._retry) {
        return Promise.reject(error);
      }

      const requestConfig = originalRequest as RequestConfig | undefined;
      if (requestConfig?.skipOnUnauthorized) {
        return Promise.reject(error);
      }

      if (requestConfig?.skipRefresh) {
        tokenStorage.clearTokens();
        onUnauthorized?.();
        return Promise.reject(error);
      }

      // Outra requisição já está fazendo refresh → enfileira e retenta com o novo token.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(newToken => {
          if (originalRequest) {
            return retryWithToken(originalRequest, newToken as string);
          }
          return Promise.reject(error);
        });
      }

      // Se houve refresh bem-sucedido recentemente, apenas retenta com o token atual do storage
      // (evita múltiplos refreshes em sequência que podem invalidar o refresh token)
      const timeSinceLastRefresh = Date.now() - lastSuccessfulRefresh;
      if (lastSuccessfulRefresh > 0 && timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
        const currentToken = tokenStorage.getAccessToken();
        if (currentToken && originalRequest) {
          return retryWithToken(originalRequest, currentToken);
        }
        return Promise.reject(error);
      }

      // Primeiro 401: inicia refresh e depois retenta esta requisição (ou rejeita se não tiver config).
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        processQueue(null, newAccessToken);

        if (originalRequest) {
          return retryWithToken(originalRequest, newAccessToken);
        }
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        const status = (refreshError as AxiosError)?.response?.status;
        const isRefreshUnauthorized = status === 401;
        const hasNoRefreshToken =
          refreshError instanceof Error &&
          refreshError.message === 'No refresh token available';
        if (isRefreshUnauthorized || hasNoRefreshToken) {
          tokenStorage.clearTokens();
          onUnauthorized?.();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return {
    get: <T = unknown>(url: string, requestConfig?: RequestConfig) =>
      client.get<T>(url, requestConfig).then(r => r.data),
    post: <T = unknown>(
      url: string,
      data?: unknown,
      requestConfig?: RequestConfig
    ) => client.post<T>(url, data, requestConfig).then(r => r.data),
    put: <T = unknown>(
      url: string,
      data?: unknown,
      requestConfig?: RequestConfig
    ) => client.put<T>(url, data, requestConfig).then(r => r.data),
    patch: <T = unknown>(
      url: string,
      data?: unknown,
      requestConfig?: RequestConfig
    ) => client.patch<T>(url, data, requestConfig).then(r => r.data),
    delete: <T = unknown>(url: string, requestConfig?: RequestConfig) =>
      client.delete<T>(url, requestConfig).then(r => r.data),
    /** Instância axios para casos em que precise de mais controle (headers, response completo, etc). */
    instance: client,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
